import { useState, useEffect, useRef, createContext, useContext, ReactNode } from 'react';
import { apiClient } from '@/api/client';
import { toast } from 'sonner';
import { migrateLocalStorageCharacters } from '@/utils/migrateCharacters';
import { ensureCacheVersion } from '@/utils/cacheVersion';
import { syncUserDataAfterOnlineOrLogin } from '@/api/userDataSyncCoordinator';
import { PENDING_REFERRAL_STORAGE_KEY } from '@/utils/referral';
import { clearAuthSession, isGuestUser } from '@/utils/authSession';

interface PushPreferences {
  sessions: boolean;
  campaign: boolean;
  chat_mentions: boolean;
  chat_all: boolean;
  activity: boolean;
  billing: boolean;
}

export interface User {
  id: string;
  email: string;
  username: string;
  plan?: 'free' | 'pro';
  created_at: string;
  subscription_status?: string | null;
  subscription_id?: string | null;
  character_slot_limit?: number | null;
  campaign_slot_limit?: number | null;
  homebrew_slot_limit?: number | null;
  spellbook_slot_limit?: number | null;
  referred_by_user_id?: number | null;
  referral_promo_ends_at?: string | null;
  push_preferences?: PushPreferences;
  account_kind?: 'account' | 'guest';
  is_guest?: boolean;
  guest_expires_at?: string | null;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, username: string, referralCode?: string) => Promise<void>;
  logout: () => void;
  refetchUser: () => Promise<void>;
  /** Has a server JWT session (rare after M10 — guests cleared; prefer hasRealAccount). */
  isAuthenticated: boolean;
  /** Historic guest JWT still in memory (should be uncommon). */
  isGuest: boolean;
  /** Signed in with a permanent account — required for campaigns / cloud sync. */
  hasRealAccount: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const wasOfflineRef = useRef(false);

  const applyAuthFromStorage = () => {
    const storedToken = localStorage.getItem('auth_token');
    const storedUser = localStorage.getItem('auth_user');
    if (!storedToken || !storedUser) {
      setToken(null);
      setUser(null);
      return false;
    }
    try {
      const parsed = JSON.parse(storedUser) as User;
      if (isGuestUser(parsed)) {
        clearAuthSession();
        setToken(null);
        setUser(null);
        return false;
      }
      setToken(storedToken);
      setUser(parsed);
      return true;
    } catch {
      clearAuthSession();
      setToken(null);
      setUser(null);
      return false;
    }
  };

  const verifyToken = async (tokenToVerify: string) => {
    try {
      setIsLoading(false);
      const response = await apiClient.get('/api/auth/verify', {
        headers: {
          Authorization: `Bearer ${tokenToVerify}`,
        },
      });
      if (isGuestUser(response.user)) {
        clearAuthSession();
        setToken(null);
        setUser(null);
        return;
      }
      setUser(response.user);
      localStorage.setItem('auth_user', JSON.stringify(response.user));
    } catch (error) {
      const status = (error as any)?.status;
      // Only clear auth state when the token is actually invalid/expired.
      // During server restarts/deploys, transient network errors should not log users out.
      if (status === 401 || status === 403) {
        clearAuthSession();
        setToken(null);
        setUser(null);
      }
    }
  };

  useEffect(() => {
    setIsLoading(false);

    ensureCacheVersion().catch(() => {});

    const storedToken = localStorage.getItem('auth_token');
    const storedUser = localStorage.getItem('auth_user');

    if (storedToken && storedUser) {
      try {
        const parsed = JSON.parse(storedUser) as User;
        if (isGuestUser(parsed)) {
          clearAuthSession();
          setToken(null);
          setUser(null);
          return;
        }
        setToken(storedToken);
        setUser(parsed);
        verifyToken(storedToken).catch(() => {});
      } catch {
        clearAuthSession();
        setToken(null);
        setUser(null);
      }
    }
    // No stored account → stay anonymous (local-only). Do not POST /api/auth/guest.
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onAuthUpdated = () => {
      applyAuthFromStorage();
    };
    window.addEventListener('mythkit:auth-updated', onAuthUpdated);
    return () => window.removeEventListener('mythkit:auth-updated', onAuthUpdated);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onOffline = () => {
      wasOfflineRef.current = true;
    };
    const onOnline = () => {
      const afterOffline = wasOfflineRef.current;
      if (wasOfflineRef.current) {
        wasOfflineRef.current = false;
      }
      if (!hasRealAccountFrom(user)) return;
      syncUserDataAfterOnlineOrLogin({ afterOfflineTransition: afterOffline }).catch(() => {});
    };
    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
    };
  }, [user]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!user || !token || isGuestUser(user)) return;

    // App-load/login sync for real accounts (serialized with other sync work).
    syncUserDataAfterOnlineOrLogin({ afterOfflineTransition: false }).catch(() => {});
  }, [user, token]);

  const refetchUser = async () => {
    const storedToken = localStorage.getItem('auth_token');
    if (storedToken) {
      await verifyToken(storedToken);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await apiClient.post('/api/auth/login', { email, password });
      const { user: userData, token: authToken } = response;

      setUser(userData);
      setToken(authToken);
      localStorage.setItem('auth_token', authToken);
      localStorage.setItem('auth_user', JSON.stringify(userData));

      const hasMigrated = localStorage.getItem('characters_migrated');
      if (!hasMigrated) {
        try {
          const result = await migrateLocalStorageCharacters();
          if (result.migrated > 0) {
            localStorage.setItem('characters_migrated', 'true');
            toast.success(`Migrated ${result.migrated} character(s) to cloud`);
          }
        } catch {
          // Don't block login if migration fails
        }
      }

      // F07: push local anon/demo data to the server after account login.
      await syncUserDataAfterOnlineOrLogin({ afterOfflineTransition: false }).catch(() => {});

      toast.success('Logged in successfully');
    } catch (error: any) {
      const message = error.message || 'Failed to log in';
      toast.error(message);
      throw error;
    }
  };

  const register = async (email: string, password: string, username: string, referralCode?: string) => {
    try {
      const payload: Record<string, string> = { email, password, username };
      if (referralCode != null && String(referralCode).trim()) {
        payload.referralCode = String(referralCode).trim();
      }
      // Always create a real account (guest upgrade disabled under M10 kill-switch).
      const response = await apiClient.post('/api/auth/register', payload);
      const { user: userData, token: authToken } = response;

      setUser(userData);
      setToken(authToken);
      localStorage.setItem('auth_token', authToken);
      localStorage.setItem('auth_user', JSON.stringify(userData));
      try {
        sessionStorage.removeItem(PENDING_REFERRAL_STORAGE_KEY);
      } catch {
        // ignore
      }

      // F07: local → cloud after register.
      await syncUserDataAfterOnlineOrLogin({ afterOfflineTransition: false }).catch(() => {});

      toast.success('Account created successfully');
    } catch (error: any) {
      const message = error.message || 'Failed to create account';
      toast.error(message);
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    clearAuthSession();
    toast.success('Logged out successfully');
  };

  const guest = isGuestUser(user);
  const realAccount = !!user && !guest;

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoading,
        login,
        register,
        logout,
        refetchUser,
        isAuthenticated: realAccount,
        isGuest: guest,
        hasRealAccount: realAccount,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

function hasRealAccountFrom(user: User | null) {
  return !!user && !isGuestUser(user);
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
